import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { taskService, type FeExpense } from '@/lib/services/taskService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type Props = {
  taskId: number
  isDisabled?: boolean
  isReadOnly?: boolean
}

export default function FeExpenseReviewPanel({ taskId, isReadOnly = false }: Props) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(true)
  const [expenses, setExpenses] = useState<FeExpense[]>([])
  const [actionId, setActionId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await taskService.listExpenses(taskId)
      setExpenses(res.expenses)
    } catch (e) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [taskId, showError])

  useEffect(() => {
    void load()
  }, [load])

  const handleReview = async (expenseId: number, action: 'approve' | 'reject') => {
    setActionId(expenseId)
    try {
      if (action === 'approve') {
        await taskService.approveExpense(taskId, expenseId)
        showSuccess('Expense approved')
      } else {
        await taskService.rejectExpense(taskId, expenseId)
        showSuccess('Expense rejected')
      }
      await load()
    } catch (e) {
      showError(e as Error)
    } finally {
      setActionId(null)
    }
  }

  if (loading) {
    return (
      <HStack py={6} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color="gray.500">Loading expenses…</Text>
      </HStack>
    )
  }

  if (expenses.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <Text fontSize="sm" color={colors.text}>No expense submissions for this task.</Text>
      </Box>
    )
  }

  return (
    <VStack align="stretch" spacing={3}>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Category</Th>
            <Th>Amount</Th>
            <Th>Status</Th>
            <Th>Submitted</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {expenses.map((expense) => (
            <Tr key={expense.id}>
              <Td>{expense.category}</Td>
              <Td>{expense.currency ?? 'PHP'} {expense.amount}</Td>
              <Td><Badge colorScheme={expense.status === 'approved' ? 'green' : expense.status === 'rejected' ? 'red' : 'yellow'}>{expense.status}</Badge></Td>
              <Td>{expense.created_at ? new Date(expense.created_at).toLocaleString() : '—'}</Td>
              <Td>
                {!isReadOnly && expense.status === 'submitted' ? (
                  <HStack spacing={2}>
                    <Button size="xs" colorScheme="green" isLoading={actionId === expense.id} onClick={() => void handleReview(expense.id, 'approve')}>Approve</Button>
                    <Button size="xs" colorScheme="red" variant="outline" isLoading={actionId === expense.id} onClick={() => void handleReview(expense.id, 'reject')}>Reject</Button>
                  </HStack>
                ) : expense.status === 'submitted' ? (
                  <Text fontSize="xs" color="gray.500">Pending review</Text>
                ) : (
                  <Text fontSize="xs" color="gray.500">Reviewed</Text>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </VStack>
  )
}
